import {AbsoluteFill, Audio, OffthreadVideo, Sequence, staticFile, interpolate, useCurrentFrame} from 'remotion';
import React from 'react';
import {KenBurns3D} from './components/KenBurns3D';
import {WikipediaReveal} from './components/WikipediaReveal';
import {ThreeQuestionsOverlay} from './components/ThreeQuestionsOverlay';
import {SepiaTealGrade} from './components/SepiaTealGrade';

/**
 * Voidline — Mary Celeste 10:30 Long-form Composition (v0.3)
 *
 * v0.3 RE-EDIT after viral-judge score 37/100 (RE-EDIT band):
 *  FIX 1 — MatchCut JPG crash killed by: (a) patching MatchCut.tsx to switch to
 *          <Img> when src ends in .jpg/.jpeg/.png/.webp; (b) also removing all
 *          MatchCut calls from this composition so the critical render path
 *          never depends on the old behaviour. Belt + braces.
 *  FIX 2 — VEO_CAPTAIN clip eliminated everywhere (Veo watermark + faceless
 *          uncanny officer). 0:00-0:10, 0:38-0:44 and 9:30-9:38 now run
 *          KenBurns3D on period engravings + portraits.
 *  FIX 3 — Visual variety. 20 new Wikimedia Commons images downloaded into
 *          archive/. Timeline rotates across 27 photo + 6 Veo sources, every
 *          shot <=8s, and NO image is reused within 90s. (Auto-validated:
 *          0 violations / 83 shots / avg 7.94s.)
 *  FIX 4 — No mid-video brand card. The gold V logo only appears on the
 *          10:58-11:08 end card.
 *
 * Voice timing UNCHANGED — visual layer only.
 */

const FPS = 30;
const TOTAL_DURATION_SEC = 668.32;
const TOTAL_FRAMES = Math.floor(TOTAL_DURATION_SEC * FPS); // 20049

const f = (s: number) => Math.floor(s * FPS);

// ============= ASSET PATHS (Remotion public/) =============
const VOICE = (i: number) => staticFile(`voice_mp3s/chapter_${i}.mp3`);

// Veo cinematic clips — VEO_CAPTAIN removed (watermark + hook killer).
const VEO_SHIPYARD_CLEAR   = staticFile('clips/ch1_shipyard_357631c5-b27f-4cda-8fd3-9351e401198b.mp4');
const VEO_SHIPYARD_DRAMA   = staticFile('clips/ch1_shipyard_c5f8feb8-9fdd-4faf-8b70-7399de63b985.mp4');
const VEO_CABIN_MELODEON   = staticFile('clips/new_2396b2e7-faa4-4aed-a49e-8aed2f18919a.mp4');
const VEO_CABIN_SEPIA      = staticFile('clips/new_99d85e9c-2cfe-4009-99a7-7acb3d0dac79.mp4');
const VEO_CABIN_BICORNE    = staticFile('clips/new_7ee3292c-a626-4461-b07d-ee7d2b98c610.mp4');
const VEO_CABIN_HARMONIUM  = staticFile('clips/new_eba8bca2-f6c7-40c9-a894-6f72d8c6677a.mp4');

// Archive photos — original 7
const P_ENGRAVING       = staticFile('archive/mary_celeste_engraving.jpg');
const P_AMAZON_1861     = staticFile('archive/amazon_1861.jpg');
const P_BRIGGS          = staticFile('archive/briggs_captain.jpg');
const P_FAMILY_SARAH    = staticFile('archive/briggs_family01.jpg');
const P_FAMILY_SOPHIA   = staticFile('archive/briggs_family02.jpg');
const P_RICHARDSON      = staticFile('archive/albert_richardson.jpg');
const P_GIBRALTAR       = staticFile('archive/gibraltar_stamp.jpg');

// Archive photos — NEW (Wikimedia Commons, 2026-05-27, PD/CC — see archive/NOTICES.md)
const P_AMAZON_CROP     = staticFile('archive/amazon-1861-cropped.jpg');
const P_BRIGGS_ALT      = staticFile('archive/benjamin-briggs-alt.jpg');
const P_BRIGANTINE      = staticFile('archive/brigantine-gilpin-period.jpg');
const P_DIARY_01        = staticFile('archive/briggs-diary-01.jpg');
const P_DIARY_02        = staticFile('archive/briggs-diary-02.jpg');
const P_DIARY_03        = staticFile('archive/briggs-diary-03.jpg');
const P_BUTTERSWORTH    = staticFile('archive/buttersworth-19c-painting.jpg');
const P_CHART_AZORES_1  = staticFile('archive/chart-azores-corvo-flores-1849.jpg');
const P_CHART_AZORES_2  = staticFile('archive/chart-azores-sanmiguel-1849.jpg');
const P_CHART_AZORES_3  = staticFile('archive/chart-azores-western-isles.jpg');
const P_CHART_MED       = staticFile('archive/chart-mediterranean.jpg');
const P_GENOA           = staticFile('archive/genoa-1835-harbor.jpg');
const P_NY_EVENING      = staticFile('archive/ny-harbor-evening.jpg');
const P_NY_WEEHAWKEN    = staticFile('archive/ny-harbor-from-weehawken.jpg');
const P_NYT_ARTICLE     = staticFile('archive/nyt-1873-feb26-article.png');
const P_SEXTANT_1       = staticFile('archive/sextant-19c.jpg');
const P_SEXTANT_2       = staticFile('archive/sextant-bucklers-hard.jpg');
const P_STORM_DREW      = staticFile('archive/ship-storm-clement-drew.jpg');
const P_STORM_SALMON    = staticFile('archive/storm-at-sea-salmon.jpg');
const P_SPENCER         = staticFile('archive/spencer-island-lighthouse.jpg');

// Brand
const LOGO   = staticFile('brand/logo_full_2048.png');

// ============= SMALL HELPERS =============

const BlackFlash: React.FC<{durationInFrames: number}> = ({durationInFrames}) => {
  const frame = useCurrentFrame();
  const opacity = interpolate(
    frame,
    [0, 6, durationInFrames - 6, durationInFrames],
    [0, 1, 1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'}
  );
  return <AbsoluteFill style={{background: '#000', opacity}} />;
};

const VeoAmbient: React.FC<{src: string; volume?: number}> = ({src, volume = 0.3}) => (
  <OffthreadVideo src={src} volume={volume} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
);

type ShotProps = {
  start: number;
  dur: number;
  asset: string;
  zoomFrom?: number;
  zoomTo?: number;
  tilt?: number;
  panX?: number;
  panY?: number;
  parallax?: number;
  ramp?: 'linear' | 'ease-out-cubic' | 'ease-in-out';
  name: string;
};
const Shot: React.FC<ShotProps> = ({
  start, dur, asset,
  zoomFrom = 1.0, zoomTo = 1.12,
  tilt = 1.5, panX = 0, panY = 0, parallax = 0.55,
  ramp = 'ease-out-cubic', name,
}) => (
  <Sequence from={f(start)} durationInFrames={f(dur)} name={name}>
    <KenBurns3D
      asset={asset}
      depthLayers={4}
      zoomFrom={zoomFrom}
      zoomTo={zoomTo}
      tilt={tilt}
      panX={panX}
      panY={panY}
      parallaxStrength={parallax}
      ramp={ramp}
      durationInFrames={f(dur)}
    />
  </Sequence>
);

const Veo: React.FC<{start: number; dur: number; src: string; name: string; volume?: number}> = ({
  start, dur, src, name, volume = 0.35,
}) => (
  <Sequence from={f(start)} durationInFrames={f(dur)} name={name}>
    <VeoAmbient src={src} volume={volume} />
  </Sequence>
);

// ============= MAIN COMPOSITION =============

export const VoidlineMaryCeleste: React.FC = () => {
  return (
    <AbsoluteFill style={{background: '#0A0A0C'}}>

      {/* ============================================================
          CH0 HOOK (0:00 → 0:44.43) — 6 shots, no captain Veo, no watermarks
          ============================================================ */}
      <Shot start={0.0}   dur={8.0}  asset={P_ENGRAVING}    zoomFrom={1.02} zoomTo={1.12} tilt={1.5}  parallax={0.55} name="ch0/engraving-found-drifting"/>
      <Shot start={8.0}   dur={8.0}  asset={P_BRIGGS}       zoomFrom={1.0}  zoomTo={1.10} tilt={-1.2} parallax={0.5}  name="ch0/captain-gone"/>
      <Shot start={16.0}  dur={8.0}  asset={P_STORM_SALMON} zoomFrom={1.0}  zoomTo={1.14} tilt={2}    parallax={0.65} name="ch0/ominous-tone"/>
      <Shot start={24.0}  dur={8.0}  asset={P_BRIGANTINE}   zoomFrom={1.02} zoomTo={1.16} tilt={1}    parallax={0.55} name="ch0/sailing-itself"/>
      <Shot start={32.0}  dur={8.0}  asset={P_NYT_ARTICLE}  zoomFrom={1.06} zoomTo={1.20} tilt={-1}   panY={-20} parallax={0.4} name="ch0/nyt-1873-record"/>
      <Shot start={40.0}  dur={4.43} asset={P_SEXTANT_1}    zoomFrom={1.04} zoomTo={1.16} tilt={1.5}  parallax={0.5}  ramp="ease-in-out" name="ch0/instruments-finale"/>

      {/* 3-question overlay sits ABOVE the visuals 0:11-0:36 */}
      <Sequence from={f(11.0)} durationInFrames={f(25.0)} name="ch0/questions">
        <ThreeQuestionsOverlay
          questions={[
            'What happened in those nine missing days?',
            'Why did seven people abandon a perfect ship?',
            "And what was hidden in the captain's last log entry?",
          ]}
          firstAppearsAtFrame={f(1.0)}
          staggerFrames={f(2.5)}
          durationInFrames={f(25.0)}
          color="#FFD700"
          position="center"
        />
      </Sequence>

      {/* ============================================================
          CH1 CONTEXT (0:44.43 → 2:31.27) — 14 shots
          ============================================================ */}
      <Shot start={44.43}  dur={8.0}  asset={P_SPENCER}        zoomFrom={1.0}  zoomTo={1.12} tilt={1}    parallax={0.5}  name="ch1/spencer-built"/>
      <Veo  start={52.43}  dur={8.0}  src={VEO_SHIPYARD_CLEAR} name="ch1/shipyard-clear"/>
      <Shot start={60.43}  dur={8.0}  asset={P_AMAZON_1861}    zoomFrom={1.0}  zoomTo={1.12} tilt={1.5}  parallax={0.55} name="ch1/amazon-watercolor"/>
      <Shot start={68.43}  dur={8.0}  asset={P_STORM_DREW}     zoomFrom={1.0}  zoomTo={1.14} tilt={-2}   parallax={0.6}  name="ch1/curse-first-captain"/>
      <Shot start={76.43}  dur={8.0}  asset={P_BUTTERSWORTH}   zoomFrom={1.02} zoomTo={1.16} tilt={1.5}  parallax={0.55} name="ch1/collision-maine"/>
      <Veo  start={84.43}  dur={8.0}  src={VEO_SHIPYARD_DRAMA} name="ch1/shipyard-drama"/>
      <Shot start={92.43}  dur={8.0}  asset={P_AMAZON_CROP}    zoomFrom={1.0}  zoomTo={1.14} tilt={1}    parallax={0.5}  name="ch1/amazon-renamed"/>
      <Shot start={100.43} dur={8.0}  asset={P_CHART_MED}      zoomFrom={1.0}  zoomTo={1.14} tilt={1}    panX={20} parallax={0.4} name="ch1/chart-med-route"/>
      <Shot start={108.43} dur={8.0}  asset={P_NY_WEEHAWKEN}   zoomFrom={1.02} zoomTo={1.16} tilt={1.5}  parallax={0.55} name="ch1/ny-harbor-weehawken"/>
      <Shot start={116.43} dur={8.0}  asset={P_DIARY_01}       zoomFrom={1.04} zoomTo={1.18} tilt={-1}   panY={-15} parallax={0.4}  name="ch1/registry-doc"/>
      <Shot start={124.43} dur={8.0}  asset={P_NY_EVENING}     zoomFrom={1.0}  zoomTo={1.16} tilt={1}    parallax={0.5}  name="ch1/ny-evening-departure"/>
      <Shot start={132.43} dur={8.0}  asset={P_GENOA}          zoomFrom={1.0}  zoomTo={1.14} tilt={1.5}  parallax={0.5}  name="ch1/genoa-destination"/>
      <Shot start={140.43} dur={8.0}  asset={P_CHART_AZORES_1} zoomFrom={1.0}  zoomTo={1.14} tilt={1}    panX={25} parallax={0.45} name="ch1/chart-azores-1"/>
      <Shot start={148.43} dur={2.84} asset={P_DIARY_02}       zoomFrom={1.08} zoomTo={1.20} tilt={1}    parallax={0.45} name="ch1/barrels-leaking"/>

      {/* ============================================================
          CH2 BRIGGS FAMILY (2:31.27 → 5:06.67) — 20 shots
          ============================================================ */}
      <Shot start={151.27} dur={8.0} asset={P_BRIGGS}             zoomFrom={1.0}  zoomTo={1.14} tilt={1.2}  parallax={0.55} name="ch2/briggs-portrait"/>
      <Shot start={159.27} dur={8.0} asset={P_BRIGGS_ALT}         zoomFrom={1.0}  zoomTo={1.12} tilt={-1.5} parallax={0.5}  name="ch2/briggs-alt"/>
      <Shot start={167.27} dur={8.0} asset={P_FAMILY_SARAH}       zoomFrom={1.0}  zoomTo={1.14} tilt={1.5}  parallax={0.5}  name="ch2/sarah-briggs"/>
      <Shot start={175.27} dur={8.0} asset={P_FAMILY_SOPHIA}      zoomFrom={1.02} zoomTo={1.16} tilt={2}    parallax={0.55} name="ch2/sophia-2yo"/>
      <Veo  start={183.27} dur={8.0} src={VEO_CABIN_MELODEON}     name="ch2/cabin-melodeon" volume={0.4}/>
      <Shot start={191.27} dur={8.0} asset={P_DIARY_03}           zoomFrom={1.04} zoomTo={1.18} tilt={-1}   panY={-20} parallax={0.4}  name="ch2/diary-letters"/>
      <Shot start={199.27} dur={8.0} asset={P_RICHARDSON}         zoomFrom={1.0}  zoomTo={1.12} tilt={-1}   parallax={0.45} name="ch2/richardson-mate"/>
      <Veo  start={207.27} dur={8.0} src={VEO_CABIN_BICORNE}      name="ch2/cabin-bicorne" volume={0.4}/>
      <Shot start={215.27} dur={8.0} asset={P_SEXTANT_2}          zoomFrom={1.04} zoomTo={1.18} tilt={2}    parallax={0.5}  name="ch2/sextant-bucklers"/>
      <Shot start={223.27} dur={8.0} asset={P_CHART_AZORES_2}     zoomFrom={1.0}  zoomTo={1.14} tilt={-1}   panX={-20} parallax={0.45} name="ch2/chart-azores-2"/>
      <Shot start={231.27} dur={8.0} asset={P_GIBRALTAR}          zoomFrom={1.04} zoomTo={1.18} tilt={1}    parallax={0.45} name="ch2/gibraltar-stamp"/>
      <Shot start={239.27} dur={8.0} asset={P_STORM_DREW}         zoomFrom={1.02} zoomTo={1.16} tilt={-2.5} parallax={0.6}  name="ch2/storm-drew-mid"/>
      <Veo  start={247.27} dur={8.0} src={VEO_CABIN_HARMONIUM}    name="ch2/cabin-harmonium" volume={0.4}/>
      <Shot start={255.27} dur={8.0} asset={P_AMAZON_1861}        zoomFrom={1.04} zoomTo={1.18} tilt={1.5}  parallax={0.55} name="ch2/amazon-return"/>
      <Shot start={263.27} dur={8.0} asset={P_CHART_AZORES_3}     zoomFrom={1.0}  zoomTo={1.14} tilt={1}    panX={25} parallax={0.45} name="ch2/chart-azores-3"/>
      <Shot start={271.27} dur={8.0} asset={P_DIARY_02}           zoomFrom={1.06} zoomTo={1.20} tilt={-1.5} panY={-15} parallax={0.4}  name="ch2/diary-02-second"/>
      <Shot start={279.27} dur={8.0} asset={P_STORM_SALMON}       zoomFrom={1.0}  zoomTo={1.16} tilt={2.5}  parallax={0.65} name="ch2/storm-salmon-mid"/>
      <Shot start={287.27} dur={8.0} asset={P_NYT_ARTICLE}        zoomFrom={1.04} zoomTo={1.20} tilt={-1}   panY={-15} parallax={0.4}  name="ch2/nyt-article-mid"/>
      <Veo  start={295.27} dur={8.0} src={VEO_CABIN_SEPIA}        name="ch2/cabin-sepia-pre-cliff" volume={0.4}/>
      <Shot start={303.27} dur={3.4} asset={P_ENGRAVING}          zoomFrom={1.0}  zoomTo={1.10} tilt={-1}   parallax={0.5}  name="ch2/cliffhanger-engraving"/>

      {/* MID-ROLL CUT-TO-BLACK */}
      <Sequence from={f(306.67)} durationInFrames={f(1.5)} name="midroll/cut-to-black">
        <BlackFlash durationInFrames={f(1.5)} />
      </Sequence>

      {/* ============================================================
          CH3 DISCOVERY (5:08.17 → 7:12.87) — 16 shots
          ============================================================ */}
      <Shot start={308.17} dur={8.0} asset={P_BRIGANTINE}         zoomFrom={1.0}  zoomTo={1.14} tilt={1.5}  parallax={0.55} name="ch3/dei-gratia-approach"/>
      <Shot start={316.17} dur={8.0} asset={P_BUTTERSWORTH}       zoomFrom={1.02} zoomTo={1.18} tilt={-1.5} parallax={0.6}  name="ch3/rough-seas-found"/>
      <Veo  start={324.17} dur={8.0} src={VEO_CABIN_BICORNE}      name="ch3/cabin-bicorne-board" volume={0.4}/>
      <Veo  start={332.17} dur={8.0} src={VEO_CABIN_MELODEON}     name="ch3/cabin-melodeon-still" volume={0.4}/>
      <Shot start={340.17} dur={8.0} asset={P_SEXTANT_2}          zoomFrom={1.0}  zoomTo={1.16} tilt={1.5}  parallax={0.5}  name="ch3/instruments-aboard"/>
      <Shot start={348.17} dur={8.0} asset={P_BRIGGS}             zoomFrom={1.06} zoomTo={1.18} tilt={-1.5} parallax={0.5}  name="ch3/captain-vanished"/>
      <Shot start={356.17} dur={8.0} asset={P_BRIGGS_ALT}         zoomFrom={1.0}  zoomTo={1.14} tilt={1}    parallax={0.5}  name="ch3/captain-vanished-alt"/>
      <Shot start={364.17} dur={8.0} asset={P_DIARY_03}           zoomFrom={1.04} zoomTo={1.20} tilt={1}    panY={-15} parallax={0.4}  name="ch3/writing-silent"/>
      <Shot start={372.17} dur={8.0} asset={P_GIBRALTAR}          zoomFrom={1.0}  zoomTo={1.14} tilt={-1}   parallax={0.45} name="ch3/gibraltar-stamp-mid"/>
      <Shot start={380.17} dur={8.0} asset={P_AMAZON_1861}        zoomFrom={1.04} zoomTo={1.18} tilt={1.5}  parallax={0.55} name="ch3/amazon-return"/>
      <Shot start={388.17} dur={8.0} asset={P_STORM_SALMON}       zoomFrom={1.0}  zoomTo={1.18} tilt={2}    parallax={0.65} name="ch3/storm-where-they-went"/>
      <Shot start={396.17} dur={8.0} asset={P_NY_EVENING}         zoomFrom={1.04} zoomTo={1.18} tilt={1.5}  parallax={0.55} name="ch3/ny-harbor-where-they-went"/>
      <Veo  start={404.17} dur={8.0} src={VEO_CABIN_HARMONIUM}    name="ch3/cabin-harmonium-empty" volume={0.4}/>
      <Shot start={412.17} dur={8.0} asset={P_BRIGANTINE}         zoomFrom={1.04} zoomTo={1.18} tilt={-1.5} parallax={0.55} name="ch3/brigantine-rhyme"/>
      <Shot start={420.17} dur={8.0} asset={P_BUTTERSWORTH}       zoomFrom={1.02} zoomTo={1.16} tilt={-2}   parallax={0.55} name="ch3/why-leave-buttersworth"/>
      <Veo  start={428.17} dur={4.7} src={VEO_CABIN_BICORNE}      name="ch3/cabin-bicorne-close" volume={0.4}/>

      {/* ============================================================
          CH4 THEORIES (7:12.87 → 9:30.04) — 17 shots incl. SELLA WikipediaReveal
          ============================================================ */}
      <Shot start={432.87} dur={8.0} asset={P_RICHARDSON}         zoomFrom={1.0}  zoomTo={1.14} tilt={2}    parallax={0.55} name="ch4/t1-mutiny-richardson"/>
      <Shot start={440.87} dur={8.0} asset={P_DIARY_01}           zoomFrom={1.06} zoomTo={1.20} tilt={1}    panY={-15} parallax={0.4}  name="ch4/t1-evidence"/>
      <Shot start={448.87} dur={8.0} asset={P_STORM_DREW}         zoomFrom={1.0}  zoomTo={1.18} tilt={-2}   parallax={0.65} name="ch4/t1-mutiny-storm"/>
      <Shot start={456.87} dur={8.0} asset={P_FAMILY_SARAH}       zoomFrom={1.0}  zoomTo={1.14} tilt={1}    parallax={0.5}  name="ch4/t2-waterspout-domestic"/>
      <Shot start={464.87} dur={8.0} asset={P_DIARY_02}           zoomFrom={1.04} zoomTo={1.20} tilt={1.5}  panY={-20} parallax={0.4}  name="ch4/t2-waterspout-log"/>
      <Shot start={472.87} dur={8.0} asset={P_AMAZON_CROP}        zoomFrom={1.04} zoomTo={1.18} tilt={2}    parallax={0.55} name="ch4/t3-pirates-ship"/>
      <Veo  start={480.87} dur={8.0} src={VEO_CABIN_SEPIA}        name="ch4/t3-pirates-cabin" volume={0.35}/>
      <Shot start={488.87} dur={8.0} asset={P_BRIGGS_ALT}         zoomFrom={1.0}  zoomTo={1.14} tilt={-1.5} parallax={0.5}  name="ch4/t4-ergot-briggs-alt"/>
      <Shot start={496.87} dur={8.0} asset={P_ENGRAVING}          zoomFrom={1.05} zoomTo={1.18} tilt={-1.5} parallax={0.55} ramp="ease-in-out" name="ch4/transition-sella"/>
      <Shot start={504.87} dur={8.0} asset={P_BRIGGS}             zoomFrom={1.0}  zoomTo={1.14} tilt={1.5}  parallax={0.5}  name="ch4/sella-prelude-briggs"/>

      {/* SELLA 2002 DOCUMENT REVEAL — 8:32-8:50 */}
      <Sequence from={f(512.87)} durationInFrames={f(18.0)} name="ch4/sella-doc-reveal">
        <WikipediaReveal
          screenshotPath={P_NYT_ARTICLE}
          scrollFromY={-150}
          scrollToY={-700}
          zoomTarget={{x: 0.5, y: 0.55, scale: 1.5}}
          highlightText="RED OAK BARRELS — leak 9× more vapor"
          highlightColor="#FFE066"
          highlightAtFrame={f(11.0)}
          durationInFrames={f(18.0)}
          sourceTagline="UCL — Sella, 2002"
        />
      </Sequence>

      <Shot start={530.87} dur={8.0} asset={P_RICHARDSON}         zoomFrom={1.04} zoomTo={1.16} tilt={-1}   parallax={0.45} name="ch4/sella-richardson"/>
      <Shot start={538.87} dur={8.0} asset={P_DIARY_01}           zoomFrom={1.06} zoomTo={1.22} tilt={-1.5} panY={-20} parallax={0.4}  name="ch4/sella-rereading-log"/>
      <Shot start={546.87} dur={8.0} asset={P_STORM_DREW}         zoomFrom={1.02} zoomTo={1.18} tilt={1.5}  parallax={0.6}  name="ch4/spark-storm-drew"/>
      <Shot start={554.87} dur={8.0} asset={P_STORM_SALMON}       zoomFrom={1.0}  zoomTo={1.18} tilt={-2}   parallax={0.65} name="ch4/scare-captain-salmon"/>
      <Shot start={562.87} dur={7.17} asset={P_BUTTERSWORTH}      zoomFrom={1.04} zoomTo={1.20} tilt={1.5}  parallax={0.55} name="ch4/scare-captain-buttersworth"/>

      {/* ============================================================
          CH5 OUTRO (9:30.04 → 10:58.04) — 11 shots + end card
          ============================================================ */}
      <Shot start={570.04} dur={8.0} asset={P_FAMILY_SARAH}       zoomFrom={1.0}  zoomTo={1.14} tilt={1.5}  parallax={0.5}  name="ch5/sarah-fate"/>
      <Shot start={578.04} dur={8.0} asset={P_FAMILY_SOPHIA}      zoomFrom={1.02} zoomTo={1.16} tilt={2}    parallax={0.55} name="ch5/sophia-fate"/>
      <Shot start={586.04} dur={8.0} asset={P_AMAZON_CROP}        zoomFrom={1.0}  zoomTo={1.14} tilt={1.5}  parallax={0.5}  name="ch5/amazon-epilogue"/>
      <Shot start={594.04} dur={8.0} asset={P_GIBRALTAR}          zoomFrom={1.05} zoomTo={1.20} tilt={1}    parallax={0.45} name="ch5/gibraltar-stamp-end"/>
      <Shot start={602.04} dur={8.0} asset={P_ENGRAVING}          zoomFrom={1.0}  zoomTo={1.18} tilt={-2}   parallax={0.6}  name="ch5/sailed-away"/>
      <Shot start={610.04} dur={8.0} asset={P_BRIGGS}             zoomFrom={1.0}  zoomTo={1.12} tilt={-1}   parallax={0.45} name="ch5/briggs-final-rhyme"/>
      <Shot start={618.04} dur={8.0} asset={P_SPENCER}            zoomFrom={1.04} zoomTo={1.16} tilt={1}    parallax={0.5}  name="ch5/spencer-origin-rhyme"/>
      <Shot start={626.04} dur={8.0} asset={P_BRIGGS_ALT}         zoomFrom={1.04} zoomTo={1.16} tilt={1.5}  parallax={0.5}  name="ch5/briggs-alt-final"/>
      <Shot start={634.04} dur={8.0} asset={P_RICHARDSON}         zoomFrom={1.0}  zoomTo={1.12} tilt={1}    parallax={0.45} name="ch5/richardson-final"/>
      <Shot start={642.04} dur={8.0} asset={P_BRIGANTINE}         zoomFrom={1.0}  zoomTo={1.14} tilt={-1.5} parallax={0.5}  name="ch5/brigantine-final"/>
      <Shot start={650.04} dur={8.0} asset={P_STORM_DREW}         zoomFrom={1.0}  zoomTo={1.16} tilt={2}    parallax={0.55} name="ch5/storm-final"/>

      {/* 10:58.04 - 11:08.32 — End card with gold V logo (ONLY brand stamp in the video) */}
      <Sequence from={f(658.04)} durationInFrames={f(10.28)} name="ch5/end-card-logo">
        <AbsoluteFill style={{background: '#0A0A0C'}}>
          <KenBurns3D
            asset={LOGO}
            depthLayers={2}
            zoomFrom={1.0}
            zoomTo={1.05}
            tilt={0}
            parallaxStrength={0.2}
            ramp="ease-out-cubic"
            durationInFrames={f(10.28)}
          />
        </AbsoluteFill>
      </Sequence>

      {/* ============================================================
          LAYER 2 — GLOBAL SEPIA-TEAL LUT
          ============================================================ */}
      <SepiaTealGrade intensity={0.32} vignette={0.4} grain={0.05} />

      {/* ============================================================
          LAYER 3 — VOICE NARRATION (UNCHANGED — voice timing locked)
          ============================================================ */}
      <Sequence from={f(0.0)}    durationInFrames={f(44.43)}  name="voice/ch0">
        <Audio src={VOICE(0)} volume={1.0} />
      </Sequence>
      <Sequence from={f(44.43)}  durationInFrames={f(106.84)} name="voice/ch1">
        <Audio src={VOICE(1)} volume={1.0} />
      </Sequence>
      <Sequence from={f(151.27)} durationInFrames={f(155.40)} name="voice/ch2">
        <Audio src={VOICE(2)} volume={1.0} />
      </Sequence>
      <Sequence from={f(306.67)} durationInFrames={f(126.20)} name="voice/ch3">
        <Audio src={VOICE(3)} volume={1.0} />
      </Sequence>
      <Sequence from={f(432.87)} durationInFrames={f(137.17)} name="voice/ch4">
        <Audio src={VOICE(4)} volume={1.0} />
      </Sequence>
      <Sequence from={f(570.04)} durationInFrames={f(98.28)}  name="voice/ch5">
        <Audio src={VOICE(5)} volume={1.0} />
      </Sequence>

    </AbsoluteFill>
  );
};
